import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Layouts from "./layouts/layout";
import { getPromoQuiz, savePromoScore } from "../services/home";
import { useBackButtonHandler } from "../hooks/useBackButtonHandler";
import styles from "./css/PromoGame2.module.css";

const extractQuestions = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.result?.data)) return res.result.data;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.result)) return res.result;
    if (Array.isArray(res?.questions)) return res.questions;
    return [];
};

const PromoGame2 = () => {
    const [data, setData] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [userScore, setUserScore] = useState(0);
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [quizData, setQuizData] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isScoreCalculated, setIsScoreCalculated] = useState(false);
    const [loadError, setLoadError] = useState("");

    const location = useLocation();

    const navigate = useNavigate();
    const perQuestion = 10;
    const answeringRef = useRef(false);
    const savedScoreRef = useRef(false);
    const advanceTimerRef = useRef(null);

    useEffect(() => {
        getPromoQuiz(location?.state.qid.id, location?.state.lavel.id, localStorage.getItem('selectedLanguage'))
            .then((res) => {
                const list = extractQuestions(res);
                const mapped = list.map((item, index) => ({
                    qno: index + 1,
                    question: item.title,
                    id: item.id,
                    quiz_id: item.quizId,
                    image: item.image,
                    type: item.type,
                    options: [
                        { key: 1, value: item.option_1 ?? item.option1 },
                        { key: 2, value: item.option_2 ?? item.option2 },
                        { key: 3, value: item.option_3 ?? item.option3 },
                        { key: 4, value: item.option_4 ?? item.option4 },
                    ].filter((opt) => opt.value),
                    correctAnswer: Number(item.correctAnswer),
                }));
                setQuizData(mapped);
                setData(true);
                if (!mapped.length) {
                    setLoadError("No questions available right now.");
                }
            })
            .catch((err) => {
                console.error(err);
                const message = err?.error || err?.message || err?.data?.error || "";
                if (
                    String(message).toLowerCase().includes("limit") ||
                    message === "Limit Reached"
                ) {
                    navigate("/promo/limit");
                    return;
                }
                setLoadError("Could not load questions. Please try again.");
                setData(true);
            });

        return () => {
            if (advanceTimerRef.current) {
                clearTimeout(advanceTimerRef.current);
            }
        };
    }, [navigate]);

    const playSound = (answer) => {
        const audio = new Audio(
            `/sounds/${answer ? "correct" : "wronganswer"}.mp3`,
        );
        audio.play().catch(() => { });
    };

    const { open } = useBackButtonHandler(true);

    useEffect(() => {
        if (open && !quizCompleted) {
            navigate("/home");
        }
    }, [open, quizCompleted, navigate]);

    useEffect(() => {
        if (!quizCompleted || savedScoreRef.current) return;

        savedScoreRef.current = true;
        savePromoScore({
            totalPoints: userScore * perQuestion,
            questions: quizData.length,
        })
            .then(() => {
                setIsScoreCalculated(true);
            })
            .catch((err) => {
                console.log(err);
                savedScoreRef.current = false;
            });
    }, [quizCompleted, userScore, quizData.length]);

    const handleAnswer = (key) => {
        if (answeringRef.current || selectedAnswer != null) return;
        answeringRef.current = true;

        const answerKey = Number(key);
        const isCorrect =
            answerKey === Number(quizData[currentQuestion]?.correctAnswer);
        setSelectedAnswer(answerKey);
        playSound(isCorrect);

        if (isCorrect) {
            setUserScore((score) => score + 1);
        }

        advanceTimerRef.current = setTimeout(() => {
            if (currentQuestion >= quizData.length - 1) {
                setQuizCompleted(true);
            } else {
                setCurrentQuestion((q) => q + 1);
                setSelectedAnswer(null);
                answeringRef.current = false;
            }
        }, 1200);
    };

    const getOptionClass = (key) => {
        const answerKey = Number(key);
        if (selectedAnswer == null) return styles.option;
        if (answerKey === Number(quizData[currentQuestion]?.correctAnswer)) {
            return `${styles.option} ${styles.optionCorrect}`;
        }
        if (answerKey === Number(selectedAnswer)) {
            return `${styles.option} ${styles.optionWrong}`;
        }
        return `${styles.option} ${styles.optionDimmed}`;
    };

    const question = quizData[currentQuestion];
    const isCorrectAnswer =
        question &&
        selectedAnswer != null &&
        Number(selectedAnswer) === Number(question.correctAnswer);
    const correctOption =
        question &&
        question.options.find(
            (opt) => Number(opt.key) === Number(question.correctAnswer),
        );

    return (
        <Layouts>
            <div className={styles.page}>
                <div className={styles.header}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                        aria-label="Go back"
                    >
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <div className={styles.scorePill}>
                        <span className={styles.scoreLabel}>Score</span>
                        <strong className={styles.scoreValue}>
                            {userScore * perQuestion}
                        </strong>
                    </div>
                </div>

                {!data && <p className={styles.loading}>Loading questions...</p>}
                {data && loadError && !quizCompleted && (
                    <p className={styles.loading}>{loadError}</p>
                )}

                {data && question && !quizCompleted && (
                    <>
                        <p className={styles.counter}>
                            Question {currentQuestion + 1} of {quizData.length}
                        </p>
                        <div className={styles.progressTrack}>
                            <div
                                className={styles.progressFill}
                                style={{
                                    width: `${((currentQuestion + 1) / quizData.length) * 100}%`,
                                }}
                            />
                        </div>

                        <div className={styles.questionCard}>
                            {question.image ? (
                                <img
                                    src={question.image}
                                    alt=""
                                    className={styles.questionImage}
                                />
                            ) : null}
                            <h3 className={styles.questionText}>{question.question}</h3>

                            <div className={styles.options}>
                                {question.options.map((opt) => (
                                    <button
                                        type="button"
                                        key={opt.key}
                                        className={getOptionClass(opt.key)}
                                        onClick={() => handleAnswer(opt.key)}
                                        disabled={selectedAnswer != null}
                                    >
                                        <span className={styles.radio} />
                                        <span className={styles.optionLabel}>{opt.value}</span>
                                        {selectedAnswer != null &&
                                            Number(opt.key) === Number(question.correctAnswer) && (
                                                <span className={styles.checkBadge}>✓</span>
                                            )}
                                    </button>
                                ))}
                            </div>

                            {selectedAnswer != null && (
                                <div
                                    className={`${styles.feedback} ${isCorrectAnswer ? styles.feedbackCorrect : styles.feedbackWrong}`}
                                >
                                    <span className={styles.feedbackIcon}>
                                        {isCorrectAnswer ? "✓" : "✕"}
                                    </span>
                                    <div>
                                        <p className={styles.feedbackTitle}>
                                            {isCorrectAnswer ? "Correct answer!" : "Wrong answer"}
                                        </p>
                                        <p className={styles.feedbackText}>
                                            {isCorrectAnswer
                                                ? `+${perQuestion} stars`
                                                : `Correct: ${correctOption?.value}`}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {quizCompleted && (
                    <div className={styles.resultCard}>
                        <div className={styles.resultTrophy}>🏆</div>
                        <h3 className={styles.resultTitle}>Game finished!</h3>
                        <p className={styles.resultScore}>
                            You scored <strong>{userScore * perQuestion} stars</strong>
                        </p>
                        <p className={styles.resultSub}>
                            {userScore}/{quizData.length} correct answers
                        </p>
                        <p className={styles.saveText}>
                            {isScoreCalculated
                                ? "Your stars have been saved."
                                : "Saving your stars..."}
                        </p>

                        <NavLink to="/promo/leaderboard" className={styles.primaryButton}>
                            See leaderboard
                        </NavLink>
                        <NavLink to="/home" className={styles.secondaryButton}>
                            Back to home
                        </NavLink>
                    </div>
                )}
            </div>
        </Layouts>
    );
};

export default PromoGame2;
